import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Sparkles, X, ArrowRight } from "lucide-react";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import type {
  DashboardNarrativeInsight,
  DashboardNarrativeSegment,
  DashboardNarrativeSegmentType,
} from "src/services/DashboardNarrativeAPI";
import "./DashboardNarrativePanel.scss";

export type DashboardNarrativeStatus = "available" | "limit_reached";

export interface DashboardNarrativeAiSummaryToggle {
  enabled: boolean;
  featureAvailable: boolean;
  usage?: { used: number; limit: number; remaining: number };
  saving?: boolean;
  setEnabled: (_enabled: boolean) => void;
}

export interface DashboardNarrativeContent {
  status?: DashboardNarrativeStatus;
  summary: string;
  summarySegments?: DashboardNarrativeSegment[];
  keyInsights?: Array<string | DashboardNarrativeInsight>;
  recommendations?: string[];
  limitTitle?: string;
  limitMessage?: string;
}

const LOADING_NARRATIVE: DashboardNarrativeContent = {
  summary: "",
};

const renderParagraphs = (paragraphs: string[], trailingAction?: JSX.Element) => {
  return paragraphs.map((paragraph, index) => (
    <p key={paragraph} className="dashboard-narrative-panel__copy">
      {renderBoldText(paragraph)}
      {index === paragraphs.length - 1 && trailingAction ? (
        <>
          {" "}
          {trailingAction}
        </>
      ) : null}
    </p>
  ));
};

const getSegmentClassName = (type: DashboardNarrativeSegmentType | undefined) => {
  if (type === "positive") {
    return "dashboard-narrative-panel__segment--positive";
  }

  if (type === "negative") {
    return "dashboard-narrative-panel__segment--negative";
  }

  return "dashboard-narrative-panel__segment--neutral";
};


const renderSummarySegments = (
  segments: DashboardNarrativeSegment[],
  trailingAction?: JSX.Element,
) => {
  return (
    <p className="dashboard-narrative-panel__copy">
      {segments.map((segment, index) => (
        <span
          key={`${segment.text}-${index}`}
          className={getSegmentClassName(segment.type)}
        >
          {segment.text}
        </span>
      ))}
      {trailingAction ? (
        <>
          {" "}
          {trailingAction}
        </>
      ) : null}
    </p>
  );
};

// `++text++` / `--text--` colors a specific number or phrase green/red inline within a
// sentence, reusing the same tokens as `summarySegments`'s positive/negative spans —
// for cases like "followers rose to ++148,500++" where only part of the line should
// carry the color, not the whole insight (that's what `DashboardNarrativeInsight.sentiment`
// controls, via the list icon).
const renderBoldText = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\+\+[^+]+\+\+|--[^-]+--)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("++") && part.endsWith("++")) {
      return (
        <span key={i} className="dashboard-narrative-panel__segment--positive">
          {part.slice(2, -2)}
        </span>
      );
    }
    if (part.startsWith("--") && part.endsWith("--")) {
      return (
        <span key={i} className="dashboard-narrative-panel__segment--negative">
          {part.slice(2, -2)}
        </span>
      );
    }
    return part;
  });
};

const renderListItems = (
  items: Array<string | DashboardNarrativeInsight>,
  trailingAction: JSX.Element | undefined,
  iconType: "sentiment" | "arrow" | "none",
  altLabels: { positive: string; negative: string },
) => {
  return items.map((item, index) => {
    const content = typeof item === "string" ? item : item.text;
    const sentiment = typeof item === "string" ? undefined : item.sentiment;

    const getIcon = () => {
      if (iconType === "none") return null;

      if (iconType === "arrow") {
        return <ArrowRight className="dashboard-narrative-panel__list-icon dashboard-narrative-panel__list-icon--arrow" size={16} />;
      }

      if (sentiment === "negative") {
        return (
          <img
            src="/Icons/Fail_Sign.svg"
            alt={altLabels.negative}
            className="dashboard-narrative-panel__list-icon dashboard-narrative-panel__list-icon--negative"
            width={20}
            height={20}
          />
        );
      }
      return (
        <img
          src="/Icons/Success_checkmark.svg"
          alt={altLabels.positive}
          className="dashboard-narrative-panel__list-icon dashboard-narrative-panel__list-icon--positive"
          width={20}
          height={20}
        />
      );
    };

    return (
      <li key={`${content}-${index}`} className={iconType !== "none" ? "dashboard-narrative-panel__list-item--with-icon" : ""}>
        {getIcon()}
        <span className="dashboard-narrative-panel__list-text">
          {renderBoldText(content)}
          {index === items.length - 1 && trailingAction ? (
            <>
              {" "}
              {trailingAction}
            </>
          ) : null}
        </span>
      </li>
    );
  });
};

interface DashboardNarrativePanelProps {
  narrative: DashboardNarrativeContent | null;
  loading?: boolean;
  defaultCollapsed?: boolean;
  title?: string;
  subtitle?: string;
  aiSummary?: DashboardNarrativeAiSummaryToggle;
}

const DashboardNarrativePanel: FC<DashboardNarrativePanelProps> = ({
  narrative,
  loading = false,
  defaultCollapsed = true,
  title,
  subtitle,
  aiSummary,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [dismissed, setDismissed] = useState(false);
  const panelTitle = title ?? t("dashboardNarrative.title");
  const panelSubtitleBase = subtitle ?? t("dashboardNarrative.subtitle");
  const iconAltLabels = {
    positive: t("dashboardNarrative.positive"),
    negative: t("dashboardNarrative.negative"),
  };

  // The whole AI summary experience is hidden for tenants without the feature.
  if (aiSummary && !aiSummary.featureAvailable) {
    return null;
  }

  const summaryDisabled = Boolean(aiSummary && !aiSummary.enabled);

  if (dismissed || (!narrative && !loading && !summaryDisabled)) {
    return null;
  }

  const panelSubtitle =
    aiSummary?.usage && Number.isFinite(aiSummary.usage.limit)
      ? `${panelSubtitleBase} • ${t("dashboardNarrative.credits", {
        used: aiSummary.usage.used,
        limit: aiSummary.usage.limit,
      })}`
      : panelSubtitleBase;

  const summarySwitch = aiSummary ? (
    <div className="dashboard-narrative-panel__switch-row">
      <span className="dashboard-narrative-panel__switch-label">
        {aiSummary.enabled ? t("dashboardNarrative.on") : t("dashboardNarrative.off")}
      </span>
      <button
        type="button"
        role="switch"
        className={`dashboard-narrative-panel__switch ${
          aiSummary.enabled ? "dashboard-narrative-panel__switch--on" : ""
        }`.trim()}
        aria-checked={aiSummary.enabled}
        aria-label={t("dashboardNarrative.toggleAria")}
        disabled={aiSummary.saving}
        onClick={() => aiSummary.setEnabled(!aiSummary.enabled)}
      >
        <span className="dashboard-narrative-panel__switch-knob" />
      </button>
    </div>
  ) : null;

  const collapseButton = (
    <button
      type="button"
      className="dashboard-narrative-panel__toggle"
      aria-label={collapsed ? t("dashboardNarrative.expandAria") : t("dashboardNarrative.collapseAria")}
      aria-expanded={!collapsed}
      onClick={() => setCollapsed(current => !current)}
    >
      {collapsed ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
    </button>
  );

  const panelHeader = (
    <div className="dashboard-narrative-panel__header">
      <div className="dashboard-narrative-panel__title-row">
        <Sparkles className="dashboard-narrative-panel__icon" size={24} />
        <div>
          <h3>{panelTitle}</h3>
          <p>{panelSubtitle}</p>
        </div>
      </div>
      <div className="dashboard-narrative-panel__actions">
        {summarySwitch}
        {collapseButton}
      </div>
    </div>
  );

  if (summaryDisabled) {
    return (
      <section className="dashboard-narrative-panel dashboard-narrative-panel--collapsed dashboard-narrative-panel--disabled">
        {panelHeader}
        <div className="dashboard-narrative-panel__body">
          <p className="dashboard-narrative-panel__copy">{t("dashboardNarrative.disabledMessage")}</p>
        </div>
      </section>
    );
  }

  const activeNarrative = narrative ?? LOADING_NARRATIVE;

  if (activeNarrative.status === "limit_reached") {
    return (
      <section className="dashboard-narrative-panel dashboard-narrative-panel--limit">
        <div className="dashboard-narrative-panel__limit-copy">
          <h3>{activeNarrative.limitTitle ?? t("dashboardNarrative.limitTitle")}</h3>
          <p>{activeNarrative.limitMessage ?? t("dashboardNarrative.limitMessage")}</p>
        </div>
        <button
          type="button"
          className="dashboard-narrative-panel__dismiss"
          aria-label={t("dashboardNarrative.dismissAria")}
          onClick={() => setDismissed(true)}
        >
          <X size={18} />
        </button>
      </section>
    );
  }

  if (loading) {
    return (
      <section
        className={`dashboard-narrative-panel ${
          collapsed ? "dashboard-narrative-panel--collapsed" : ""
        }`.trim()}
      >
        {panelHeader}

        <div className="dashboard-narrative-panel__body">
          <div className="dashboard-narrative-panel__loader" aria-live="polite">
            <CircularLoader size={32} thickness={4} />
          </div>
        </div>
      </section>
    );
  }

  const summary = activeNarrative.summary ? [activeNarrative.summary] : [];
  const summarySegments = activeNarrative.summarySegments ?? [];
  const keyInsights = activeNarrative.keyInsights ?? [];
  const recommendations = activeNarrative.recommendations ?? [];
  const hasDetails = Boolean(keyInsights.length || recommendations.length);
  const shouldShowDetails = hasDetails && !collapsed;
  const seeMoreToggle = hasDetails && collapsed ? (
    <button
      type="button"
      className="dashboard-narrative-panel__inline-toggle"
      aria-expanded={!collapsed}
      onClick={() => setCollapsed(current => !current)}
    >
      <span>{t("dashboardNarrative.seeMore")}</span>
      <ChevronDown size={14} />
    </button>
  ) : undefined;
  const seeLessToggle = hasDetails && !collapsed ? (
    <button
      type="button"
      className="dashboard-narrative-panel__inline-toggle"
      aria-expanded={!collapsed}
      onClick={() => setCollapsed(current => !current)}
    >
      <span>{t("dashboardNarrative.seeLess")}</span>
      <ChevronUp size={14} />
    </button>
  ) : undefined;

  return (
    <section
      className={`dashboard-narrative-panel ${
        collapsed ? "dashboard-narrative-panel--collapsed" : ""
      }`.trim()}
    >
      {panelHeader}

      <div className="dashboard-narrative-panel__body">
        {summarySegments.length
          ? renderSummarySegments(summarySegments, seeMoreToggle)
          : renderParagraphs(summary, seeMoreToggle)}

        {shouldShowDetails ? (
          <div className="dashboard-narrative-panel__details">
            {keyInsights.length ? (
              <div className="dashboard-narrative-panel__list-block">
                <h4 className="dashboard-narrative-panel__section-title dashboard-narrative-panel__section-title--insights">
                  {t("dashboardNarrative.keyInsights")}
                </h4>
                <ul>
                  {renderListItems(
                    keyInsights,
                    recommendations.length ? undefined : seeLessToggle,
                    "sentiment",
                    iconAltLabels,
                  )}
                </ul>
              </div>
            ) : null}

            {recommendations.length ? (
              <div className="dashboard-narrative-panel__list-block">
                <h4 className="dashboard-narrative-panel__section-title dashboard-narrative-panel__section-title--recommendations">
                  {t("dashboardNarrative.recommendedActions")}
                </h4>
                <ul>
                  {renderListItems(recommendations, seeLessToggle, "arrow", iconAltLabels)}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default DashboardNarrativePanel;
